import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { extractPdfData } from '@/lib/api';
import { FileText, Loader2, Sparkles, Database, CheckCircle2 } from 'lucide-react';

export const PdfExtractor = ({ onExtracted }) => {
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [masterMatches, setMasterMatches] = useState([]);
  const fileRef = useRef(null);

  const handleExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Bitte eine PDF-Datei auswählen');
      return;
    }

    setExtracting(true);
    setResult(null);
    setMasterMatches([]);
    try {
      const res = await extractPdfData(file);
      const data = res.data;
      if (data.extracted_data) {
        setResult(data.extracted_data);
        setMasterMatches(data.master_data_matches || []);
        if (data.master_data_match_count > 0) {
          toast.success(`Daten extrahiert – ${data.master_data_match_count} Stammdaten-Treffer gefunden`);
        } else {
          toast.success('Daten erfolgreich aus PDF extrahiert (keine Stammdaten-Treffer)');
        }
      } else {
        toast.error(data.error || 'Keine Daten extrahiert');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF-Extraktion fehlgeschlagen');
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const applyToForm = () => {
    if (result) {
      onExtracted?.(result);
      toast.success('Extrahierte Daten übernommen');
    }
  };

  const applyMasterMatch = (match) => {
    const mapped = {
      supplier_name: match.supplier_name || match.manufacturer_name,
      supplier_article_number: match.supplier_article_number || match.manufacturer_part_number,
      product_name: match.product_name || match.material_description,
      material_type: match.material_type,
      face_material: match.face_material,
      adhesive_type: match.adhesive_type,
      liner_type: match.liner_type,
      total_thickness_um: match.total_thickness_um,
      basis_weight_gm2: match.basis_weight_gm2,
      width_mm: match.width_mm,
      fsc: match.fsc,
      food_safe: match.food_safe,
    };
    // Remove undefined/null values
    const cleaned = Object.fromEntries(Object.entries(mapped).filter(([, v]) => v != null));
    onExtracted?.(cleaned);
    toast.success('Stammdaten übernommen');
  };

  const FIELD_LABELS = {
    supplier_name: 'Hersteller',
    supplier_article_number: 'Artikelnr.',
    product_name: 'Produktname',
    material_type: 'Materialtyp',
    face_material: 'Obermaterial',
    adhesive_type: 'Klebstoff',
    liner_type: 'Trägermaterial',
    total_thickness_um: 'Dicke (μm)',
    basis_weight_gm2: 'Flächengewicht (g/m²)',
    width_mm: 'Breite (mm)',
    certifications: 'Zertifizierungen',
    fsc: 'FSC',
    food_safe: 'Lebensmittelsicher',
    notes: 'Anmerkungen',
    manufacturer_name: 'Hersteller',
    manufacturer_part_number: 'Artikelnr.',
    material_description: 'Beschreibung',
    ek_price_eur: 'EK-Preis (EUR)',
  };

  return (
    <div className="space-y-3" data-testid="pdf-extractor">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={extracting}
          onClick={() => fileRef.current?.click()}
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
          data-testid="extract-pdf-btn"
        >
          {extracting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {extracting ? 'KI analysiert PDF...' : 'Daten aus PDF extrahieren'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf"
          onChange={handleExtract}
        />
      </div>

      {result && (
        <div className="border border-violet-200 dark:border-violet-800 rounded-lg p-4 bg-violet-50/50 dark:bg-violet-900/10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-violet-500" />
              Extrahierte Daten
            </h4>
            <Button
              size="sm"
              onClick={applyToForm}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="apply-extracted-data-btn"
            >
              Daten übernehmen
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {Object.entries(result).map(([key, value]) => {
              if (value === null || value === undefined || value === '') return null;
              const display = typeof value === 'boolean' ? (value ? 'Ja' : 'Nein') : String(value);
              return (
                <div key={key} className="flex justify-between py-0.5">
                  <span className="text-muted-foreground">{FIELD_LABELS[key] || key}:</span>
                  <span className="font-medium text-right truncate ml-2 max-w-[60%]">{display}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {masterMatches.length > 0 && (
        <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-900/10" data-testid="master-data-matches">
          <h4 className="font-medium text-sm flex items-center gap-1.5 mb-3">
            <Database className="h-4 w-4 text-emerald-500" />
            Stammdaten-Treffer ({masterMatches.length})
          </h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {masterMatches.map((match, i) => (
              <div key={i} className="p-3 rounded-lg border bg-white dark:bg-slate-900 text-sm" data-testid={`master-match-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {match._source === 'master_data' ? 'Stammdaten' : 'Standardmaterial'}
                    </Badge>
                    <span className="font-medium">
                      {match.supplier_name || match.manufacturer_name || 'Unbekannt'}
                    </span>
                    <span className="text-muted-foreground">
                      {match.supplier_article_number || match.manufacturer_part_number || ''}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-7 text-xs"
                    onClick={() => applyMasterMatch(match)}
                    data-testid={`apply-master-match-${i}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    Übernehmen
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-1 text-xs text-muted-foreground">
                  {match.product_name && <span>Produkt: {match.product_name}</span>}
                  {match.material_description && <span>Beschr.: {match.material_description}</span>}
                  {match.material_type && <span>Typ: {match.material_type}</span>}
                  {match.ek_price_eur && <span>EK: {match.ek_price_eur} EUR</span>}
                  {match.width_mm && <span>Breite: {match.width_mm}mm</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
